<script lang="ts" setup>
import { useAuthStore } from '@/stores/authStore'
import AdaptiveDialog from '../AdaptiveDialog.vue'
import FloatLabel from 'primevue/floatlabel'
import SrDataTable from '../tables/SrDataTable.vue'
import type { TableColumn } from '@/components/alerts/types'
import Select from 'primevue/select'
import Button from 'primevue/button'
import { nextTick, computed, onMounted, ref, watch } from 'vue'
import { useTzDate } from '@/composable/useTzDate'
import { Divider } from 'primevue'
import { useMyToast } from '@/composable/useMyToast.ts'
import { i18n } from '@/i18n.ts'
import WithdrawalRequestModal from './WithdrawalRequestModal.vue'
import UserProfilesModal from './UserProfilesModal.vue'

const { t } = i18n.global
const availableRights = ['futures', 'dex_cex']
const visible = ref(false)
const withdrawalModal = ref<InstanceType<typeof WithdrawalRequestModal> | null>(null)
const profilesModal = ref<InstanceType<typeof UserProfilesModal> | null>(null)
const authStore = useAuthStore()
const { dateFullStr } = useTzDate()
const toggle = () => {
  visible.value = !visible.value
}
defineExpose({
  toggle,
})
const accessList = computed(() => {
  return Object.entries(authStore.currentAccessList)
    .filter((value) => availableRights.includes(value[0]))
    .map((value) => {
      return {
        accessType: value[0],
        dateFrom: value[1]['date_from'],
        dateTo: value[1]['date_to'],
      }
    })
})

const { addToast } = useMyToast()
const copyCode = async () => {
  if (!authStore.refCode) return
  const refLink = `${window.location.origin}/?ref=${authStore.refCode}`
  try {
    await navigator.clipboard.writeText(refLink)
    addToast({ severity: 'success', message: t('modal.profile.codeCopied') })
  } catch {
    addToast({ severity: 'error', message: t('modal.profile.codeCopyFailed') })
  }
}

const timezone = ref()
const timezones = ref()
let init = false

const normalizeTz = (tz: string | null): string => {
  if (!tz) return '+00:00'
  if (tz === '00:00') return '+00:00'
  return tz
}

watch(timezone, async (v: string) => {
  if (!authStore.authorized || !init) {
    return
  }
  await authStore.setTimezone(v)
})

watch(() => authStore.tz, (v) => {
  if (v && !init) {
    timezone.value = normalizeTz(v)
  }
}, { immediate: true })

onMounted(async () => {
  timezones.value = [
    { title: 'UTC', value: '+00:00' },
    { title: 'UTC +10:00', value: '+10:00' },
    { title: 'UTC +09:30', value: '+09:30' },
    { title: 'UTC +09:00', value: '+09:00' },
    { title: 'UTC +08:00', value: '+08:00' },
    { title: 'UTC +07:00', value: '+07:00' },
    { title: 'UTC +06:00', value: '+06:00' },
    { title: 'UTC +05:00', value: '+05:00' },
    { title: 'UTC +04:00', value: '+04:00' },
    { title: 'UTC +03:00', value: '+03:00' },
    { title: 'UTC +02:00', value: '+02:00' },
    { title: 'UTC +01:00', value: '+01:00' },
    { title: 'UTC -01:00', value: '-01:00' },
    { title: 'UTC -02:00', value: '-02:00' },
    { title: 'UTC -03:30', value: '-03:30' },
    { title: 'UTC -04:00', value: '-04:00' },
    { title: 'UTC -05:00', value: '-05:00' },
    { title: 'UTC -06:00', value: '-06:00' },
    { title: 'UTC -08:00', value: '-08:00' },
    { title: 'UTC -09:00', value: '-09:00' },
    { title: 'UTC -10:00', value: '-10:00' },
  ]
  timezone.value = normalizeTz(authStore.tz)
  await nextTick()
  init = true
})

const showWithdrawalModal = () => {
  withdrawalModal.value?.toggle()
}

const showProfilesModal = () => {
  profilesModal.value?.toggle()
}

const columns: TableColumn[] = [
  {
    field: 'accessType',
    header: 'modal.profile.accessType',
    class: 'text-center',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: () => ({ class: 'pit' }),
  },
  {
    field: 'dateFrom',
    header: 'modal.profile.dateFrom',
    class: 'text-center',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: () => ({ class: 'pit digit' }),
  },
  {
    field: 'dateTo',
    header: 'modal.profile.dateTo',
    class: 'text-center',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: () => ({ class: 'pit digit' }),
  },
]

const formattingRules = {
  accessType: (v: string) => t(`accessList.${v}`),
  dateFrom: (v: string) => dateFullStr(v + 'Z'),
  dateTo: (v: string) => dateFullStr(v + 'Z'),
}
</script>

<template>
  <AdaptiveDialog v-model:visible="visible" pt:content="p-0" class="md:w-200 lg:w-150" pt:header="px-4 py-1"
    :header="$t('modal.profile.title')">

    <div class="p-3 flex justify-end">
      <Button size="small" severity="success" variant="outlined" @click="showProfilesModal">
        {{ $t('modal.profile.profiles') }}
      </Button>
    </div>

    <SrDataTable size="small" :value="accessList" :columns="columns" :formattingRules="formattingRules">
      <template #empty>
        <div class="flex justify-center items-center muted">{{ $t('noAccessesInList') }}</div>
      </template>
    </SrDataTable>
    <!-- <div class="mt-1 p-3">
      <div class="text-sm mb-2">
        <span class="font-semibold">{{ $t('modal.profile.profiles') }}:</span>
        CEX: {{ authStore.profiles.cex_cex.join(', ') || '-' }};
        DEX: {{ authStore.profiles.dex_cex.join(', ') || '-' }}
      </div>
    </div> -->
    <div class="mt-1 p-3 gap-2">
      <FloatLabel variant="on">
        <Select v-model="timezone" :options="timezones" optionValue="value" optionLabel="title"
          :placeholder="$t('selectTimezone')" checkmark :highlightOnSelect="false" class="w-full" />
        <label>{{ $t('timezone') }}</label>
      </FloatLabel>
    </div>
    <Divider align="left" type="solid" class="mb-0 mt-0"><span class="text-sm">{{ $t('modal.profile.refs') }}</span>
    </Divider>
    <div class="p-3 grid grid-cols-3">
      <div class="text-sm mb-2 text-center">
        <span v-tooltip.bottom="$t('tooltip.refCode')" @click="copyCode" class="pit success hover cursor-pointer">{{
          $t('modal.profile.refCode') }}: {{
            authStore.refCode || '-'
          }}</span>
      </div>
      <div class="text-sm mb-2 text-center">
        <span v-tooltip.bottom="$t('tooltip.refBalance')" @click="showWithdrawalModal"
          class="pit hover cursor-pointer success">{{ $t('modal.profile.refBalance')
          }}:
          <span class="digit">{{
            authStore.refBalance !==
              null
              ?
              `$${authStore.refBalance}` : '$0' }}</span> </span>
      </div>
      <div class="text-sm text-center">
        <span v-tooltip.bottom="$t('tooltip.refPrecent')" class="pit success">{{ $t('modal.profile.refRatio') }}: <span
            class="digit">{{ authStore.refRatio !== null
              ?
              `${authStore.refRatio}%`
              :
              '-' }}</span> </span>
      </div>
    </div>
  </AdaptiveDialog>
  <WithdrawalRequestModal ref="withdrawalModal" />
  <UserProfilesModal ref="profilesModal" />
</template>

<style scoped>
</style>
